import { useTranslate } from '@repo/shared/hooks'
import {
  Button,
  Card,
  CenterBox,
  Checkbox,
  Col,
  Icon,
  Row,
  Typography,
} from '@repo/ui/components'
import { useState, useMemo, memo } from 'react'
import { twMerge } from 'tailwind-merge'
import { CancelButton } from './selfie-kyc.cancel-button'

const confirmItems = [
  { key: 'audio', labelKey: 'selfie_kyc_confirm_audio' },
  { key: 'video', labelKey: 'selfie_kyc_confirm_video' },
]

// Memoized video component to prevent re-renders
const VideoPlayer = memo(({ videoBlob }: { videoBlob?: Blob }) => {
  const videoUrl = useMemo(() => {
    return videoBlob ? URL.createObjectURL(videoBlob) : null
  }, [videoBlob])

  if (!videoUrl) return null

  return (
    <video
      src={videoUrl}
      controls
      autoPlay
      playsInline
      className='bg-black aspect-[9/16] object-cover w-full h-full'
      style={{
        maxHeight: '100%',
        objectFit: 'cover',
        objectPosition: 'center',
      }}
    />
  )
})

VideoPlayer.displayName = 'VideoPlayer'

const SelfieKycConfirm = ({
  onSubmit,
  onBack,
  videoBlob,
  isLoading = false,
}: {
  onSubmit: () => void
  onBack: () => void
  videoBlob?: Blob
  isLoading?: boolean
}) => {
  const { t } = useTranslate()
  const [checked, setChecked] = useState<{ [k: string]: boolean }>({
    audio: false,
    video: false,
  })
  const allChecked = Object.values(checked).every(Boolean)

  return (
    <Card
      className='!p-0 overflow-hidden relative h-[100dvh] rounded-none border-none'
      style={{ maxHeight: '100dvh', minHeight: '100dvh', height: '100dvh' }}
    >
      <CancelButton />
      <CenterBox className='bg-black items-end overflow-hidden w-full h-full'>
        <VideoPlayer videoBlob={videoBlob} />
      </CenterBox>
      <Col className='p-md gap-2xl items-center'>
        <Col className='gap-md'>
          {confirmItems.map((item) => (
            <Row key={item.key} className='gap-md items-center'>
              <Checkbox
                checked={checked[item.key]}
                onCheckedChange={(v) =>
                  setChecked((prev) => ({ ...prev, [item.key]: !!v }))
                }
                size='sm'
                color='base'
                disabled={isLoading}
              />
              <Typography
                variant='span'
                size='md'
                className='color-neutral-main'
              >
                {t(item.labelKey as keyof typeof t)}
              </Typography>
            </Row>
          ))}
        </Col>
        <Row className='gap-lg justify-center'>
          {!allChecked && (
            <Button
              variant='transparent'
              size='md'
              width='auto'
              className='w-full'
              onClick={onBack}
              disabled={isLoading}
            >
              {t('selfie_kyc_confirm_back')}
            </Button>
          )}
          <Button
            variant='fill'
            color='base'
            size='md'
            width='auto'
            className={twMerge('w-full', !allChecked && 'mt-0.5')}
            onClick={onSubmit}
            disabled={!allChecked || isLoading}
            loading={isLoading}
            endIcon={
              <Icon
                name='ArrowLeftLine'
                size='md'
                color={
                  allChecked && !isLoading
                    ? '!fill-neutral-20'
                    : '!fill-neutral-surface-40'
                }
              />
            }
          >
            {t('selfie_kyc_confirm_btn')}
          </Button>
        </Row>
      </Col>
    </Card>
  )
}

export { SelfieKycConfirm }
